import { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../../firebase-config';
import { Button } from '../ui/button';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

interface ImageUploadProps {
  onImageUploaded: (url: string, storagePath: string) => void;
  onImageRemoved?: () => void;
  currentImageUrl?: string;
  currentStoragePath?: string;
  folder?: string; // e.g., 'blog-images'
  maxSizeMB?: number;
  acceptedFormats?: string[];
}

export default function ImageUpload({
  onImageUploaded,
  onImageRemoved,
  currentImageUrl,
  currentStoragePath,
  folder = 'blog-images',
  maxSizeMB = 5,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `Invalid file type. Accepted formats: ${acceptedFormats.join(', ')}`;
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File size exceeds ${maxSizeMB}MB limit`;
    }

    return null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Check authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('You must be logged in to upload images');
        setUploading(false);
        return;
      }

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${folder}/${timestamp}_${sanitizedFileName}`;

      // Create storage reference
      const storageRef = ref(storage, storagePath);

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError(`Upload failed: ${error.message}`);
          setUploading(false);
        },
        async () => {
          // Upload completed successfully
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setPreviewUrl(downloadURL);
          setUploading(false);
          setUploadProgress(0);
          onImageUploaded(downloadURL, storagePath);
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentStoragePath) {
      setPreviewUrl(null);
      if (onImageRemoved) onImageRemoved();
      return;
    }

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, currentStoragePath);
      await deleteObject(storageRef);

      setPreviewUrl(null);
      if (onImageRemoved) onImageRemoved();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {!previewUrl && !uploading && (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-muted rounded-full">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Upload featured image</p>
              <p className="text-xs text-muted-foreground">
                {acceptedFormats.map(f => f.split('/')[1]).join(', ').toUpperCase()} • Max {maxSizeMB}MB
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Image
            </Button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium">Uploading...</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">{Math.round(uploadProgress)}%</p>
        </div>
      )}

      {/* Image Preview */}
      {previewUrl && !uploading && !imageLoadError && (
        <div className="relative border border-border rounded-lg overflow-hidden">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-64 object-cover"
            onError={() => {
              setImageLoadError(true);
              setError('Failed to load image. You can remove it and upload a new one.');
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemove}
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      )}

      {/* Broken Image Fallback */}
      {previewUrl && !uploading && imageLoadError && (
        <div className="border border-destructive rounded-lg p-6 bg-destructive/5">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-destructive/10 rounded-full">
              <X className="h-8 w-8 text-destructive" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-destructive mb-1">Image failed to load</p>
              <p className="text-xs text-muted-foreground">The image may have been deleted or is inaccessible</p>
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => {
                setPreviewUrl(null);
                setImageLoadError(false);
                setError(null);
                if (onImageRemoved) onImageRemoved();
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Remove Broken Image
            </Button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
          {error}
        </div>
      )}

      {/* SEO Tips */}
      <div className="bg-muted/50 rounded-lg p-4 text-sm">
        <p className="font-medium mb-2">Image SEO Tips:</p>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
          <li>Use descriptive filenames (e.g., "product-feedback-loop-guide.jpg")</li>
          <li>Recommended size: 1200×630px for optimal social sharing</li>
          <li>Keep file size under 500KB for faster loading</li>
          <li>Add descriptive alt text in the form below</li>
        </ul>
      </div>
    </div>
  );
}
